/**
 * /settings/workspace — Workspace section wrapper (#851).
 *
 * Header + context bar (`Workspace · <name>`) + pill sub-nav +
 * `<Outlet context={...} />`. Threads the active workspace down
 * via Outlet context so tab components stay zero-prop at the
 * Route level and receive their inputs through `useOutletContext`.
 *
 * Visibility: per-tab `gate` lists the workspace roles for which
 * the tab renders. Org Owners get implicit Workspace Admin per
 * ADR 0010, resolved here so the visibility predicate doesn't have
 * to know about implicit grants.
 *
 * The card-index helper `getVisibleWorkspaceTabs(role)` is what the
 * Settings landing page uses to decide whether to render the
 * Workspace card at all — card shows iff at least one tab is
 * visible for the user's role. Workspace Reviewer ends up with
 * zero visible tabs (no gate includes 'reviewer') so the card
 * hides entirely, matching ADR 0010 decision 3.
 */
import { useMemo } from 'react'
import { Navigate, Outlet, useOutletContext } from 'react-router-dom'
import type { WorkspaceSummary } from '@/lib/api'
import { useAppContext } from '@/hooks/useAppContext'
import { useIdentity } from '@/hooks/useIdentity'
import { SettingsSubNav, type SettingsTab } from '../SettingsSubNav'

type WorkspaceRole = 'admin' | 'tester' | 'reviewer'

interface WorkspaceTabDef extends SettingsTab {
  /** Roles for which this tab renders. Org Owners are folded into
   * 'admin' by the caller (implicit grant); the gate list itself
   * only enumerates direct workspace roles. */
  gate: readonly WorkspaceRole[]
}

// #1153 collapsed the workspace tab strip from 5 entries to 4 — the
// standalone "Notifications" tab merged into "Integrations", which
// now lists both GitHub and Slack provider connections as sibling
// cards. The per-app binding (which repo, which channel) lives on
// each app's Integrations tab; per-variant routing lives in the App
// Integrations tab's per-variant drawer (Slice 2 of meta #1152 + the
// drawer-replaces-Automation work in Slice 1 of meta #1183). The
// `'integrations'` key here is intentionally stable — it has been
// declared since before the PR-#1145 detour into 'ci-cd' and is
// what the wrapper has ALWAYS rendered. The CI/CD slug exists only
// as the App.tsx redirect alias for in-flight bookmarks.
const WORKSPACE_TABS: readonly WorkspaceTabDef[] = [
  { to: 'general',       label: 'General',       gate: ['admin'] },
  { to: 'members',       label: 'Members',       gate: ['admin'] },
  { to: 'integrations',  label: 'Integrations',  gate: ['admin'] },
  { to: 'danger',        label: 'Danger',        gate: ['admin'], danger: true },
]

export interface WorkspaceOutletContext {
  workspace: WorkspaceSummary
}

/** Tab-list filter for a given effective workspace role. Pure;
 * used by both the wrapper (to render the sub-nav) and the Settings
 * landing page (to decide whether the Workspace card shows). */
export function getVisibleWorkspaceTabs(
  effectiveRole: WorkspaceRole | undefined,
): readonly SettingsTab[] {
  if (!effectiveRole) return []
  return WORKSPACE_TABS.filter((t) => t.gate.includes(effectiveRole))
}

export function WorkspaceWrapper() {
  const { workspace } = useAppContext()
  const { state } = useIdentity()

  // ADR 0010 §"Org Owner implicit Admin reach" — owners get implicit
  // workspace admin on every workspace in their org. Resolve here
  // so the visibility predicate doesn't have to know.
  const effectiveRole = useMemo<WorkspaceRole | undefined>(() => {
    if (state.status !== 'ready' || !workspace?._id) return undefined
    const orgRole = state.identity.orgs.find((o) => o._id === workspace.orgId)?.role
    if (orgRole === 'owner') return 'admin'
    return state.identity.workspaceRoles?.[workspace._id]
  }, [state, workspace?._id, workspace?.orgId])

  const visibleTabs = useMemo(
    () => getVisibleWorkspaceTabs(effectiveRole),
    [effectiveRole],
  )

  if (!workspace || state.status !== 'ready') return null

  // No visible tabs → user landed on Workspace via direct URL but
  // their role admits nothing here. Redirect back to the cards
  // index (which will hide the Workspace card for them anyway).
  if (visibleTabs.length === 0) {
    return <Navigate to="/settings" replace />
  }

  const ctx: WorkspaceOutletContext = { workspace }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">
          Workspace <span className="text-muted-foreground font-normal">· {workspace.name}</span>
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Settings scoped to the currently-active workspace.
        </p>
      </div>
      <SettingsSubNav tabs={visibleTabs} ariaLabel="Workspace settings sub-navigation" />
      <div className="pt-2">
        <Outlet context={ctx} />
      </div>
    </div>
  )
}

/** Thin hook for tab components — extracts the workspace from
 * Outlet context. Mirrors react-router's `useOutletContext` with
 * a section-specific type narrow so per-tab call sites don't need
 * to repeat the generic. */
export function useWorkspaceOutletContext(): WorkspaceOutletContext {
  return useOutletContext<WorkspaceOutletContext>()
}

// ── Tab route adapters ────────────────────────────────────────────────
// Each adapter is a tiny component bridging Outlet context to the
// existing tab content component's prop interface. Lives here (not
// in App.tsx) so the wrapper that owns the context also owns the
// shape conversion — App.tsx stays a pure route map.
//
// Auth fixtures + Notifications are stubs pending #570 / #571
// respectively; the tab structure ships now so deep-links + the
// sub-nav are stable across the upstream design work landing.

import { WorkspaceGeneral } from '@/pages/WorkspaceSettings/General'
import { WorkspaceMembers } from '@/pages/WorkspaceSettings/Members'
import { WorkspaceIntegrations } from '@/pages/WorkspaceSettings/Integrations'
import { WorkspaceDanger } from '@/pages/WorkspaceSettings/Danger'
import { NotificationsSlackChannelPicker } from '@/pages/WorkspaceSettings/NotificationsSlackChannelPicker'

export function WorkspaceGeneralRoute() {
  const { workspace } = useWorkspaceOutletContext()
  return <WorkspaceGeneral workspace={workspace} />
}
export function WorkspaceMembersRoute() {
  const { workspace } = useWorkspaceOutletContext()
  return <WorkspaceMembers workspace={workspace} />
}
// The Slack channel-picker route survives the #1153 consolidation —
// it's the destination of the post-OAuth wizard hop (mounted at
// `workspace/notifications/slack/channels` in App.tsx), so its host
// component stays even though the workspace-level Notifications page
// it returned to was absorbed into Integrations. The picker's "back"
// links now point at the Integrations canonical path.
export function WorkspaceNotificationsSlackChannelsRoute() {
  const { workspace } = useWorkspaceOutletContext()
  return <NotificationsSlackChannelPicker workspace={workspace} />
}
export function WorkspaceIntegrationsRoute() {
  const { workspace } = useWorkspaceOutletContext()
  return <WorkspaceIntegrations workspace={workspace} />
}
export function WorkspaceDangerRoute() {
  const { workspace } = useWorkspaceOutletContext()
  return <WorkspaceDanger workspace={workspace} />
}
